import React, { useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'

import PremiumDetailsModal from './PremiumDetailsModal'
import Spinner from 'components/Spinner'
import useActivePremiumAccount from 'hooks/useActivePremiumAccount'
import usePaywallPremiumAcct from 'hooks/usePaywallPremiumAcct'
import useScreenSize from 'hooks/useScreenSize'

import { useFeaturesContext } from 'hooks/FeaturesContext'
import { useSessionContext } from 'hooks/SessionContext'
import { usePremiumFeaturesContext } from 'hooks/PremiumFeaturesContext'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'
import GA4 from 'helpers/ga4'

type Props = {
  variant?: 'small' | 'large'
  className?: string
}

const PremiumWidget = ({ variant = 'large', className }: Props) => {
  const {
    session: {
      isDealer,
      isDealerAdmin,
      isTechnician,
      dealerId
    }
  } = useSessionContext()
  const [showPremiumFeaturesDialog, setShowPremiumFeaturesDialog] = useState(false)
  const { premiumFreeTrial } = usePremiumFeaturesContext()
  const { enabledFeatures } = useFeaturesContext()
  const { activeAccount, isActiveAccountLoaded } = useActivePremiumAccount(dealerId)
  const { premiumAccount, isPremiumAcctLoaded } = usePaywallPremiumAcct(dealerId)
  const screenSize = useScreenSize()
  const navigate = useNavigate()

  const title = (() => {
    if (isPremiumAcctLoaded && premiumAccount) {
      return premiumFreeTrial ? 'Premium Trial in Progress' : 'Premium Account'
    } else if (enabledFeatures.includes('paywall_trial') && !premiumAccount) {
      return 'Try Premium for Free'
    } else {
      return 'Upgrade to Premium'
    }
  })()

  const imageWidth = screenSize === 'small' ? '32' : '28'

  const onPaywallClick = () => {
    if (premiumAccount && isPremiumAcctLoaded) {
      const planType = premiumAccount.planId === 'diag_monthly' ? 'Monthly' : 'Yearly'

      GA4.sendEvent({
        action: 'Click Premium Settings',
        category: 'Premium Settings',
        label: `Plan Type: ${planType}`
      })

      navigate(`/paywall/details/${dealerId}`)
    } else {
      GA4.sendEvent({
        action: 'Click Upgrade to Premium',
        category: 'Premium Settings'
      })

      navigate(`/paywall/options/${dealerId}`)
    }
  }

  const onFreeTrialClick = () => {
    if (premiumAccount && isPremiumAcctLoaded) {
      setShowPremiumFeaturesDialog(true)
    }
  }

  const hasRoleAndPremium = (isDealer || isTechnician) && activeAccount
  const shouldRender =
    enabledFeatures.includes('paywall') &&
    (hasRoleAndPremium || isDealerAdmin) &&
    isActiveAccountLoaded && isPremiumAcctLoaded

  if (!shouldRender) {
    return null
  }

  if (variant === 'large') {
    return (
      <S.Container className={className} data-testid="large variant premium widget">
        <Button
          styles={largeAndMediumScreenButtonStyle((premiumAccount && isPremiumAcctLoaded) || premiumFreeTrial)}
          onPress={isDealerAdmin ? onPaywallClick : onFreeTrialClick}
        >
          <S.CrownIcon src="/img/premium_crown.png" width={imageWidth} />
          <S.ButtonText isPremiumActive={(premiumAccount && isPremiumAcctLoaded) || premiumFreeTrial}>{title}</S.ButtonText>
        </Button>
        <PremiumDetailsModal isVisible={showPremiumFeaturesDialog} closeCallback={() => setShowPremiumFeaturesDialog(false)} />
      </S.Container>
    )
  } else if (variant === 'small') {
    return (
      <S.SmallScreenContainer className={className} data-testid="small variant premium widget">
        <Button styles={smallScreenButtonStyle} onPress={onPaywallClick}>
          <S.CrownIcon src="/img/premium_crown.png" width={imageWidth} />
        </Button>
      </S.SmallScreenContainer>
    )
  }
}

export default PremiumWidget

const largeAndMediumScreenButtonStyle = (isPremium: boolean): ButtonStyles => {
  if (isPremium) {
    return {
      button: {
        borderTopLeftRadius: 17,
        borderBottomLeftRadius: 17,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        backgroundColor: color('premiumWidgetActiveButton'),
        borderColor: color('premiumWidgetActiveButton')
      }
    }
  } else {
    return {
      button: {
        borderTopLeftRadius: 17,
        borderBottomLeftRadius: 17,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        backgroundColor: color('premiumWidgetInactiveButton'),
        borderColor: color('premiumWidgetInactiveButton')
      }
    }
  }
}

const smallScreenButtonStyle: ButtonStyles = {
  button: {
    width: 26,
    minWidth: 0,
    borderRadius: 100
  }
}

const S = {
  Container: styled.div`
    position: fixed;
    right: 0;
    margin-left: auto;
    top: 30px;
    width: max-content;
    z-index: 1000;
  `,
  SmallScreenContainer: styled.div`
  `,
  ButtonText: styled.span.withConfig({
    shouldForwardProp: (prop) => prop !== 'isPremiumActive'
  })<{ isPremiumActive: boolean }>`
    color: ${({ isPremiumActive }) => isPremiumActive ? color('premiumWidgetActiveButtonText') : color('primaryButtonText')};
  `,
  CrownIcon: styled.img`
    margin-left: -6px;
    margin-bottom: .5px;
    margin-right: 9px;
    border: ${color('premiumWidgetActiveButtonText')} 1px solid;
    border-radius: 100%;

    @media (${screenSize.small}) {
      margin: 0 10px 0 0;
    }
  `,
  Spinner: styled(Spinner)`
    margin: 10px auto;
    height: 30px;
    width: 30px;
  `
}
